import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function formatDate(date) {
  if (!date) return "";
  try {
    const d = new Date(date);
    return isNaN(d.getTime()) ? date : d.toLocaleDateString("en-GB");
  } catch (e) {
    return date;
  }
}

const DEFAULT_CENTER = [41.3874, 2.1686];

const MarkerClusterGroup = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    if (!markers || markers.length === 0) return;

    const mcg = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyDistanceMultiplier: 2,
    });

    markers.forEach((locationData) => {
      if (locationData.eventCount > 1) {
        const titles = locationData.titles || [locationData.title];

        titles.forEach((title, titleIndex) => {
          const angle = (titleIndex / titles.length) * Math.PI * 2;
          const radius = 0.00005;

          const lat = locationData.lat + radius * Math.cos(angle);
          const lng = locationData.lng + radius * Math.sin(angle);

          const marker = L.marker([lat, lng], { icon: defaultIcon });
          const date = locationData.dates?.[titleIndex];

          marker.bindPopup(
            '<div style="min-width: 220px; font-family: inherit;">' +
              '<strong style="font-size: 14px; color: #111;">' + title + '</strong>' +
              (date ? '<br /><small style="color: #666;">📅 ' + formatDate(date) + '</small>' : '') +
              (locationData.location_original ? '<br /><br /><div style="font-size: 12px; color: #444;">' + locationData.location_original + '</div>' : '') +
              '<br /><small style="color: #888;"><em>' + titles.length + ' items at this location</em></small>' +
            '</div>'
          );

          mcg.addLayer(marker);
        });
      } else {
        const marker = L.marker([locationData.lat, locationData.lng], { icon: defaultIcon });

        marker.bindPopup(
          '<div style="min-width: 220px; font-family: inherit;">' +
            '<strong style="font-size: 14px; color: #111;">' + locationData.title + '</strong>' +
            (locationData.date ? '<br /><small style="color: #666;">📅 ' + formatDate(locationData.date) + '</small>' : '') +
            (locationData.location_original ? '<br /><br /><div style="font-size: 12px; color: #444;">' + locationData.location_original + '</div>' : '') +
          '</div>'
        );

        mcg.addLayer(marker);
      }
    });

    map.addLayer(mcg);

    if (markers.length > 0) {
      const allLats = markers.map((m) => m.lat);
      const allLngs = markers.map((m) => m.lng);

      const bounds = L.latLngBounds(
        [[Math.min(...allLats), Math.min(...allLngs)], 
         [Math.max(...allLats), Math.max(...allLngs)]]
      );

      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
      });
    }

    return () => {
      map.removeLayer(mcg);
    };
  }, [markers, map]);

  return null;
};

const PostsMap = ({ posts }) => {
  const [markers, setMarkers] = useState([]);
  const [center, setCenter] = useState(DEFAULT_CENTER);

  useEffect(() => {
    const postsArray = Array.isArray(posts) ? posts : posts?.data || [];

    if (!postsArray.length) {
      setMarkers([]);
      return;
    }

    const locationMap = new Map();

    postsArray.forEach((post) => {
      const lat = parseFloat(post.lat);
      const lng = parseFloat(post.lng);

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const key = lat.toFixed(5) + "," + lng.toFixed(5);
        const title = post.title || post.name || "Location";
        const desc = post.location || post.description || post.address || "";
        const date = post.start_date || post.post_date || null;

        if (locationMap.has(key)) {
          const existing = locationMap.get(key);
          existing.eventCount += 1;
          existing.titles.push(title);
          if (date) existing.dates.push(date);
        } else {
          locationMap.set(key, {
            lat,
            lng,
            title,
            titles: [title],
            dates: date ? [date] : [],
            eventCount: 1,
            date,
            location_original: desc,
          });
        }
      }
    });

    const found = Array.from(locationMap.values());
    setMarkers(found);

    if (found.length > 0) {
      setCenter([found[0].lat, found[0].lng]);
    }
  }, [posts]);

  const totalPoints = markers.reduce((sum, m) => sum + (m.eventCount || 1), 0);

  return (
    <div>
      {markers.length > 0 && (
        <div
          style={{
            marginBottom: "10px",
            padding: "8px 12px",
            background: "#f8f9fa",
            border: "1px solid #e9ecef",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#495057",
          }}
        >
          📍 <strong>{totalPoints}</strong> spots plotted on map across <strong>{markers.length}</strong> locations
        </div>
      )}

      <MapContainer
        center={center}
        zoom={12}
        style={{ height: "450px", width: "100%", borderRadius: "8px" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors, HOT"
        />

        <MarkerClusterGroup markers={markers} />
      </MapContainer>
    </div>
  );
};

export default PostsMap;
